import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { 
  Car, 
  MapPin, 
  ChevronRight, 
  CheckCircle2, 
  ShieldCheck, 
  Zap, 
  Users, 
  Upload, 
  MessageSquare, 
  PhoneCall,
  Clock,
  TrendingUp,
  Star,
  ChevronDown,
  Sparkles,
  Search
} from 'lucide-react';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);




const SellYourCar = () => {
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    fuelType: '',
    kmDriven: '',
    city: '',
    mobile: ''
  });

  const [faqOpen, setFaqOpen] = useState(null);
  const [images, setImages] = useState({
    Front: null,
    Back: null,
    Interior: null
  });

  const [showValuation, setShowValuation] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [valuationData, setValuationData] = useState({
    min: 0,
    max: 0,
    average: 0
  });

  const [inspectionState, setInspectionState] = useState({
    type: 'Home', // Home or Center
    date: '',
    slot: '',
    address: ''
  });

  const [bookingStatus, setBookingStatus] = useState(false);
  const [showMonthList, setShowMonthList] = useState(false);
  const [showYearList, setShowYearList] = useState(false);


  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  
  const slots = ['10:00 AM - 12:00 PM', '12:00 PM - 02:00 PM', '02:00 PM - 04:00 PM', '04:00 PM - 06:00 PM'];


  const [condition, setCondition] = useState({
    serviceHistory: 'Authorized', // Authorized, Local, None
    accidents: 'No', // No, Minor, Major
    acCooling: 'Excellent',
    tyreCondition: 'Good',
    loan: 'No',
    features: []
  });

  const [rcImage, setRcImage] = useState(null);
  const [isRcVerified, setIsRcVerified] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const recentlySold = [
    { model: "Honda City V", year: "2019", price: "7.85L", time: "2 hrs ago", city: "Delhi", condition: "Excellent" },
    { model: "Maruti Swift LXi", year: "2021", price: "5.10L", time: "5 hrs ago", city: "Mumbai", condition: "Good" },
    { model: "Hyundai Creta SX", year: "2020", price: "12.4L", time: "8 hrs ago", city: "Bangalore", condition: "Mint" },
    { model: "Toyota Fortuner", year: "2018", price: "24.5L", time: "1 day ago", city: "Pune", condition: "Great" },
    { model: "Tata Nexon XZ", year: "2022", price: "9.20L", time: "1 day ago", city: "Chennai", condition: "Excellent" },
  ];

  const availableFeatures = [
    "Sunroof", "Touchscreen", "Reverse Camera", "Leather Seats", "Cruise Control", "Alloy Wheels"
  ];

  const handleFeatureToggle = (feature) => {
    setCondition(prev => ({
      ...prev,
      features: prev.features.includes(feature) 
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleBookInspection = () => {
    if (!inspectionState.date || !inspectionState.slot || !inspectionState.address) {
      toast.error("Please select a date, slot and provide an address!");
      return;
    }
    setBookingStatus(true);
    setTimeout(() => {
      toast.success("Inspection Scheduled Successfully!", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
      setBookingStatus(false);
    }, 2000);
  };

  const handleGetPrice = () => {
    if (!formData.brand || !formData.model || !formData.year) {
      toast.error("Please fill in basic car details first!");
      return;
    }

    setIsCalculating(true);
    
    // Simulate API call/Calculation
    setTimeout(() => {
      // Mock calculation logic
      const basePrice = 500000; // Base for a standard car
      const yearFactor = (parseInt(formData.year) - 2010) * 50000;
      const brandFactor = formData.brand === 'Honda' ? 1.2 : 1.0;
      
      const estimatedValue = (basePrice + yearFactor) * brandFactor;
      
      setValuationData({
        min: estimatedValue * 0.9,
        max: estimatedValue * 1.1,
        average: estimatedValue
      });
      
      setIsCalculating(false);
      setShowValuation(true);
      
      // Scroll to valuation result
      document.getElementById('valuation-result')?.scrollIntoView({ behavior: 'smooth' });
    }, 1500);
  };


  const steps = [
    { icon: <Car className="w-8 h-8" />, title: "Enter Car Details", desc: "Share basic info about your car to get started." },
    { icon: <TrendingUp className="w-8 h-8" />, title: "Get Price Estimate", desc: "Receive an instant, fair market price estimate." },
    { icon: <Users className="w-8 h-8" />, title: "Connect with Buyers", desc: "We link you with thousands of verified buyers." },
    { icon: <ShieldCheck className="w-8 h-8" />, title: "Sell & Get Paid", desc: "Complete the paperwork and receive instant payment." },
  ];

  const features = [
    { title: "Free Listing", desc: "No hidden charges or upfront fees for your car listing.", color: "bg-blue-50 text-blue-600" },
    { title: "Verified Buyers", desc: "Direct access to 5000+ background-checked professional buyers.", color: "bg-red-50 text-red-600" },
    { title: "Fast Process", desc: "Our sellers average a successful sale within just 48 hours.", color: "bg-blue-50 text-blue-600" },
    { title: "Secure Transaction", desc: "Safe payment handling and assistance with RC transfers.", color: "bg-red-50 text-red-600" },
  ];

  const testimonials = [
    { name: "Rahul Sharma", role: "Sold Honda City", rating: 5, text: "The process was incredibly smooth. Got a better price than local dealers!", avatar: "https://i.pravatar.cc/150?u=1" },
    { name: "Priya Patel", role: "Sold Hyundai Creta", rating: 4, text: "Very professional team. Sold my car in just 2 days. Highly recommended.", avatar: "https://i.pravatar.cc/150?u=2" },
    { name: "Anish Kumar", role: "Sold Maruti Swift", rating: 5, text: "Fast payment and hassle-free paperwork. The best way to sell your car.", avatar: "https://i.pravatar.cc/150?u=3" },
  ];

  const { scrollYProgress } = useScroll();
  const carRotate = useTransform(scrollYProgress, [0, 0.2], [0, -15]);
  const carScale = useTransform(scrollYProgress, [0, 0.2], [1, 1.1]);
  const carX = useTransform(scrollYProgress, [0, 0.2], [0, 50]);
  
  const scanLineRef = useRef(null);

  useEffect(() => {
    if (showValuation && scanLineRef.current) {
      gsap.to(scanLineRef.current, {
        y: 300,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });
    }
  }, [showValuation]);

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 overflow-x-hidden selection:bg-blue-600 selection:text-white">
      {/* Immersive Scroll Progress */}
      <motion.div 
        className="fixed top-0 left-0 right-0 h-1.5 bg-blue-600 origin-left z-[100]"
        style={{ scaleX: scrollYProgress }}
      />

      {/* 1. Hero Section - Redesigned for Suggestion 1 */}
      <section className="relative pt-24 pb-48 lg:pt-36 lg:pb-64 bg-[#0a0f1d] overflow-hidden">
        {/* Futuristic Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-red-600/10 rounded-full blur-[140px]"></div>
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
        </div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-20">
            <div className="w-full lg:w-3/5 space-y-10">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-400 text-sm font-black uppercase tracking-widest mb-6 backdrop-blur-md">
                  <Sparkles className="w-4 h-4" />
                  <span>The Future of Car Selling is Here</span>
                </div>
                
                <h1 className="text-6xl lg:text-[6.5rem] font-black leading-[0.9] tracking-tighter text-white">
                  SELL YOUR <br/>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-600 to-red-600">CAR IN MINUTES</span>
                </h1>

                
                <p className="text-xl lg:text-2xl text-slate-400 mt-8 max-w-2xl font-medium leading-relaxed">
                  Powered by <span className="text-white font-bold">SparePe AI</span>. Get instant verified offers, 
                  doorstep inspection, and lightning-fast payment.
                </p>

                <div className="flex flex-wrap gap-6 mt-12">
                  <motion.button 
                    whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(220, 38, 38, 0.3)" }}
                    whileTap={{ scale: 0.95 }}
                    className="px-10 py-5 bg-red-600 text-white rounded-2xl font-black text-xl shadow-2xl transition-all flex items-center gap-3"
                  >
                    Sell My Car Now
                    <ChevronRight className="w-6 h-6" />
                  </motion.button>
                  <button className="px-10 py-5 bg-white/5 border border-white/10 text-white backdrop-blur-md rounded-2xl font-black text-xl hover:bg-white/10 transition-all">
                    Explore Marketplace
                  </button>
                </div>

                <div className="flex items-center gap-10 mt-16 pt-10 border-t border-white/5">
                   <div className="flex flex-col">
                      <span className="text-3xl font-black text-white">100K+</span>
                      <span className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1 text-center">Active Sellers</span>
                   </div>
                   <div className="w-px h-10 bg-white/10"></div>
                   <div className="flex flex-col">
                      <span className="text-3xl font-black text-white">₹500Cr+</span>
                      <span className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-1 text-center">Value Processed</span>
                   </div>
                </div>
              </motion.div>
            </div>

            <div className="w-full lg:w-2/5 relative">
              <motion.div
                style={{ rotateY: carRotate, scale: carScale, x: carX }}
                className="relative z-10 perspective-1000"
              >
                <img 
                  src="/luxury_car_garage_1775651737392.png" 
                  alt="Hero Car" 
                  className="w-[120%] h-auto drop-shadow-[0_50px_50px_rgba(37,99,235,0.3)] rounded-[4rem] -ml-20"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&q=80&w=800";
                  }}
                />
                
                {/* Floating UI Elements over car */}
                <motion.div 
                  className="absolute -top-10 -right-10 p-6 bg-white/10 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-2xl"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: -1 }}
                >
                   <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                         <ShieldCheck className="w-6 h-6 text-white" />
                      </div>
                      <div>
                         <p className="text-[10px] text-slate-400 font-bold uppercase">Condition</p>
                         <p className="text-white font-black">98% Verified</p>
                      </div>
                   </div>
                </motion.div>
              </motion.div>
              
              {/* Dynamic Aura */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-blue-600/20 rounded-full blur-[120px] -z-10"></div>
            </div>
          </div>
        </div>
      </section>


      {/* 2. Quick Car Details Form (Sticky Overlay) */}
      <div className="container mx-auto px-4 -mt-24 lg:-mt-32 relative z-30">
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-3xl shadow-2xl p-8 lg:p-10 border border-slate-100"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-8 bg-blue-600 rounded-full"></div>
            <h2 className="text-2xl font-bold">Quick Valuation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-500">Car Brand</label>
              <div className="relative">
                <select 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl appearance-none focus:ring-2 focus:ring-blue-500 transition-all"
                  value={formData.brand}
                  onChange={(e) => setFormData({...formData, brand: e.target.value})}
                >
                  <option value="">Select Brand</option>
                  <option value="Honda">Honda</option>
                  <option value="Toyota">Toyota</option>
                  <option value="Maruti Suzuki">Maruti Suzuki</option>
                  <option value="Hyundai">Hyundai</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-500">Model</label>
              <input 
                type="text" 
                placeholder="e.g. City" 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all"
                value={formData.model}
                onChange={(e) => setFormData({...formData, model: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-500">Year</label>
              <input 
                type="number" 
                placeholder="e.g. 2022" 
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-500">City</label>
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Select City" 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                />
                <MapPin className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map(i => (
                  <img key={i} src={`https://i.pravatar.cc/150?u=${i+10}`} className="w-10 h-10 rounded-full border-2 border-white" alt="user" />
                ))}
              </div>
              <p className="text-sm text-slate-500"><span className="font-bold text-slate-900">12,400+</span> people checked prices today</p>
            </div>
            <button 
              onClick={handleGetPrice}
              disabled={isCalculating}
              className={`w-full md:w-auto px-12 py-4 text-white font-bold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 ${isCalculating ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'}`}
            >
              {isCalculating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Analyzing Market...
                </>
              ) : (
                "Get Instant Price"
              )}
            </button>
          </div>
        </motion.div>

        {/* Dynamic Valuation Result Card */}
        {showValuation && (
          <motion.div 
            id="valuation-result"
            className="mt-8 bg-slate-900 rounded-[2.5rem] p-8 lg:p-12 text-white overflow-hidden relative"
          >

            {/* Background Decorative Element */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full blur-[100px] opacity-20 -mr-32 -mt-32"></div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm font-bold border border-blue-500/30">
                  <TrendingUp className="w-4 h-4" />
                  <span>Real-time Market Value</span>
                </div>
                <h3 className="text-3xl lg:text-4xl font-bold">Estimated Worth of your <span className="text-blue-400">{formData.model}</span></h3>
                
                <div className="flex flex-col gap-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl lg:text-6xl font-black text-white">₹{(valuationData.average/100000).toFixed(2)}L</span>
                    <span className="text-slate-400 text-lg">Expected Price</span>
                  </div>
                  <p className="text-slate-400">Based on recent sales of similar {formData.brand} cars in your city.</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-8">
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/10">
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Minimum Offer</p>
                    <p className="text-xl font-bold text-white">₹{(valuationData.min/100000).toFixed(2)}L</p>
                  </div>
                  <div className="bg-white/5 p-4 rounded-2xl border border-white/10">
                    <p className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Maximum Offer</p>
                    <p className="text-xl font-bold text-blue-400">₹{(valuationData.max/100000).toFixed(2)}L</p>
                  </div>
                </div>

                <div className="pt-4 flex flex-wrap gap-4">
                  <button 
                    onClick={() => document.getElementById('inspection-section')?.scrollIntoView({ behavior: 'smooth' })}
                    className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-red-900/50"
                  >
                    Book Inspection
                  </button>
                  <button className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all border border-white/10">
                    See Report Details
                  </button>
                </div>
              </div>

              <div className="relative h-64 lg:h-80 flex items-center justify-center">
                {/* Custom SVG Price Gauge */}
                <div className="relative w-64 h-64 lg:w-80 lg:h-80">
                  <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                    <circle
                      cx="50" cy="50" r="45"
                      fill="none"
                      stroke="rgba(255,255,255,0.05)"
                      strokeWidth="8"
                    />
                    <motion.circle
                      cx="50" cy="50" r="45"
                      fill="none"
                      stroke="url(#gaugeGradient)"
                      strokeWidth="8"
                      strokeDasharray="282.7"
                      initial={{ strokeDashoffset: 282.7 }}
                      animate={{ strokeDashoffset: 282.7 - (282.7 * 0.75) }} // Fill 75% for good value
                      transition={{ duration: 1.5, delay: 0.5 }}
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#2563eb" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <div className="text-green-400 flex items-center gap-1 font-bold mb-1">
                      <TrendingUp className="w-4 h-4" />
                      <span>+4.2%</span>
                    </div>
                    <p className="text-3xl lg:text-4xl font-black">GOOD</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em]">Market Health</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* 2.5 Professional Inspection Booking Section */}
        <section id="inspection-section" className="mt-20 scroll-mt-24">
          <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 flex flex-col lg:flex-row">
            {/* Left Side: Illustration & Info */}
            <div className="w-full lg:w-1/3 bg-blue-600 p-12 text-white flex flex-col justify-between">
              <div>
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <MapPin className="w-8 h-8" />
                </div>
                <h3 className="text-3xl font-bold mb-4">Book Your Free Home Inspection</h3>
                <p className="text-blue-100 leading-relaxed">
                  Our professional engineers will visit your location to verify car condition and finalize the offer.
                </p>
              </div>
              
              <div className="space-y-4 mt-8 lg:mt-0">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-400 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-blue-900" />
                  </div>
                  <span className="text-sm font-medium">150+ Point Checklist</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-400 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-blue-900" />
                  </div>
                  <span className="text-sm font-medium">Zero Cancellation Fees</span>
                </div>
              </div>
            </div>

            {/* Right Side: Scheduler */}
            <div className="w-full lg:w-2/3 p-8 lg:p-12 space-y-10 bg-slate-50/50">
              {/* Inspection Type Toggle */}
              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-500 uppercase tracking-wider">Select Inspection Type</label>
                <div className="flex bg-slate-200 p-1 rounded-2xl w-fit">
                   {['Home', 'Center'].map(t => (
                     <button 
                        key={t}
                        onClick={() => setInspectionState({...inspectionState, type: t})}
                        className={`px-8 py-3 rounded-xl font-bold transition-all ${inspectionState.type === t ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'}`}
                     >
                       {t} Inspection
                     </button>
                   ))}
                </div>
              </div>

              {/* Full Calendar with Month/Year Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-500 uppercase tracking-wider">Select Inspection Date</label>
                  {inspectionState.date && (
                    <motion.div 
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="px-4 py-1.5 bg-blue-600 text-white rounded-full text-xs font-black shadow-lg shadow-blue-200 flex items-center gap-2"
                    >
                      <Clock className="w-3.5 h-3.5" />
                      SELECTED: {format(selectedCalendarDate, 'PPPP')}
                    </motion.div>
                  )}
                </div>
                <div className="relative bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm overflow-hidden group">
                  <DatePicker

                    selected={selectedCalendarDate}
                    onChange={(date) => {
                      setSelectedCalendarDate(date);
                      setInspectionState({...inspectionState, date: format(date, 'yyyy-MM-dd')});
                    }}
                    minDate={new Date()}
                    inline
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    calendarClassName="full-modern-calendar"
                    renderCustomHeader={({
                      date,
                      changeYear,
                      changeMonth,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled,
                    }) => (
                      <div className="flex items-center justify-between px-2 py-4 bg-white border-b border-slate-100 mb-4">
                        <button
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                          type="button"
                          className="p-2 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-30"
                        >
                          <ChevronRight className="w-5 h-5 rotate-180 text-blue-600" />
                        </button>
                        
                        <div className="flex gap-3 px-2 py-1 bg-slate-100/50 rounded-2xl border border-slate-200/50 relative z-[100]">
                          {/* Custom Year Dropdown */}
                          <div className="relative">
                            <button 
                              type="button"
                              onClick={() => { setShowYearList(!showYearList); setShowMonthList(false); }}
                              className="bg-white border border-slate-200 text-slate-900 font-black px-4 py-2 pr-10 rounded-xl outline-none cursor-pointer flex items-center justify-between shadow-sm transition-all hover:bg-slate-50 text-sm min-w-[100px]"
                            >
                              <span>{date.getFullYear()}</span>
                              <ChevronDown className={`absolute right-3 w-4 h-4 text-slate-400 transition-transform ${showYearList ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {showYearList && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute top-full left-0 mt-2 bg-white border border-slate-100 shadow-2xl rounded-2xl p-2 min-w-full max-h-[250px] overflow-y-auto scrollbar-hide z-[110]"
                              >
                                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map((year) => (
                                  <button
                                    key={year}
                                    type="button"
                                    onClick={() => { changeYear(year); setShowYearList(false); }}
                                    className={`w-full text-left px-4 py-2 rounded-xl text-sm font-bold transition-all ${date.getFullYear() === year ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'}`}
                                  >
                                    {year}
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </div>

                          {/* Custom Month Dropdown */}
                          <div className="relative">
                            <button 
                              type="button"
                              onClick={() => { setShowMonthList(!showMonthList); setShowYearList(false); }}
                              className="bg-white border border-slate-200 text-slate-900 font-black px-4 py-2 pr-10 rounded-xl outline-none cursor-pointer flex items-center justify-between shadow-sm transition-all hover:bg-slate-50 text-sm min-w-[130px]"
                            >
                              <span>{format(date, 'MMMM')}</span>
                              <ChevronDown className={`absolute right-3 w-4 h-4 text-slate-400 transition-transform ${showMonthList ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {showMonthList && (
                              <motion.div 
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute top-full left-0 mt-2 bg-white border border-slate-100 shadow-2xl rounded-2xl p-2 min-w-full max-h-[250px] overflow-y-auto scrollbar-hide z-[110]"
                              >
                                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                                  <button
                                    key={month}
                                    type="button"
                                    onClick={() => { changeMonth(idx); setShowMonthList(false); }}
                                    className={`w-full text-left px-4 py-2 rounded-xl text-sm font-bold transition-all ${format(date, 'MMMM') === month ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-700'}`}
                                  >
                                    {month}
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </div>
                        </div>



                        <button
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                          type="button"
                          className="p-2 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-30"
                        >
                          <ChevronRight className="w-5 h-5 text-blue-600" />
                        </button>
                      </div>
                    )}
                  />
                </div>
              </div>


              {/* Slot Selection */}
              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-500 uppercase tracking-wider">Available Time Slots</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                   {slots.map(s => (
                     <button
                        key={s}
                        onClick={() => setInspectionState({...inspectionState, slot: s})}
                        className={`px-6 py-4 rounded-xl border-2 text-left font-bold transition-all ${inspectionState.slot === s ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300'}`}
                     >
                        {s}
                     </button>
                   ))}
                </div>
              </div>

              {/* Address Input */}
              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-500 uppercase tracking-wider">Inspection Address</label>
                <div className="relative">
                  <textarea 
                    placeholder="Enter your full address where our engineer should visit..."
                    className="w-full px-6 py-4 rounded-2xl border-2 border-slate-200 focus:border-blue-600 outline-none transition-all bg-white resize-none"
                    rows="3"
                    value={inspectionState.address}
                    onChange={(e) => setInspectionState({...inspectionState, address: e.target.value})}
                  ></textarea>
                  <MapPin className="absolute right-4 top-4 text-slate-400" />
                </div>
              </div>

              <div className="pt-4">
                <button 
                  onClick={handleBookInspection}
                  disabled={bookingStatus}
                  className={`w-full py-5 rounded-2xl font-black text-xl text-white shadow-xl flex items-center justify-center gap-3 transition-all ${bookingStatus ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200 translate-y-0 active:translate-y-1'}`}
                >
                  {bookingStatus ? (
                     <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      Confirm Appointment
                      <ChevronRight className="w-6 h-6" />
                    </>
                  )}
                </button>
                <p className="text-center text-slate-400 text-sm mt-4 italic">No obligation to sell. Inspection is 100% free.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* 3. How It Works Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Selling your car has never been this simple. Just 4 easy steps to get the real value of your vehicle.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {steps.map((step, index) => (
              <div key={index} className="relative text-center group">
                {index < 3 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full border-t-2 border-dashed border-slate-100 -z-10"></div>
                )}
                <div className="w-24 h-24 bg-white border border-slate-100 rounded-3xl shadow-xl flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-600 transition-all transform group-hover:-translate-y-2 group-hover:text-white">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
                <div className="mt-4 inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-400 font-bold text-sm">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Price Trust / Featured Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="bg-blue-600 rounded-[3rem] p-12 lg:p-20 relative overflow-hidden flex flex-col lg:flex-row items-center gap-12">
            <div className="relative z-10 w-full lg:w-3/5 space-y-6">
              <h2 className="text-3xl lg:text-5xl font-bold text-white leading-tight">
                No Middlemen. <br />
                Just <span className="text-red-400 italic">Better</span> Prices.
              </h2>
              <ul className="space-y-4">
                {["Get best market price estimate based on real data", "Direct connection to verified retail buyers", "100% Free listing with zero success fees"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white">
                    <CheckCircle2 className="w-6 h-6 text-blue-300" />
                    <span className="text-lg opacity-90">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-6">
                <button className="px-10 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-slate-100 transition-all shadow-xl">
                  Try Price Estimator
                </button>
              </div>
            </div>
            <div className="w-full lg:w-2/5">
              <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20">
                <div className="flex justify-between items-end mb-6">
                  <div>
                    <p className="text-blue-100 text-sm">Market Trend</p>
                    <p className="text-white font-bold text-2xl">Value is Up! 🚀</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-100 text-sm">Last 30 Days</p>
                    <p className="text-green-400 font-bold">+4.5%</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-4 bg-white/5 p-3 rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-white/10"></div>
                      <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-300 rounded-full" style={{ width: `${30 + i * 20}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-white/60 text-xs mt-6 text-center italic">Updated 5 minutes ago</p>
              </div>
            </div>
            {/* Background elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 opacity-20"></div>
          </div>
        </div>
      </section>

      {/* 4.5 Recently Sold Live Feed - NEW SECTION */}
      <section className="py-24 bg-slate-900 overflow-hidden relative">
        {/* Animated Background Glow */}
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[500px] h-[500px] bg-red-600/10 rounded-full blur-[120px]"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                 <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-black uppercase tracking-widest">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                    Live Sales Feed
                 </div>
              </div>
              <h2 className="text-4xl font-bold text-white">Recently Sold on <span className="text-red-600">SparePe</span></h2>
              <p className="text-slate-400 max-w-xl">Real cars, real prices. See what sellers in your network are making today.</p>
            </div>
            
            <div className="hidden md:flex gap-4">
               <div className="text-right">
                  <p className="text-2xl font-black text-white">₹1.2Cr+</p>
                  <p className="text-xs text-slate-500 font-bold uppercase">Paid out this week</p>
               </div>
            </div>
          </div>

          <div className="flex gap-6 overflow-x-auto pb-8 scrollbar-hide no-scrollbar -mx-4 px-4 mask-fade-edges">
            {recentlySold.map((sale, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex-shrink-0 w-80 bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-[2.5rem] hover:bg-white/10 transition-all group"
              >
                <div className="flex justify-between items-start mb-6">
                  <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-all">
                    <Car className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 bg-green-500/10 text-green-500 text-[10px] font-black uppercase rounded-full border border-green-500/20">
                    {sale.condition}
                  </span>
                </div>
                
                <div className="space-y-1">
                  <h4 className="text-xl font-bold text-white">{sale.model}</h4>
                  <p className="text-slate-400 text-sm">{sale.year} • {sale.city}</p>
                </div>

                <div className="mt-8 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-bold uppercase mb-1">Sold For</p>
                    <p className="text-2xl font-black text-blue-400">₹{sale.price}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-slate-500 font-bold italic">{sale.time}</p>
                  </div>
                </div>
              </motion.div>
            ))}
            
            {/* View More Card */}
            <div className="flex-shrink-0 w-64 bg-blue-600/10 border border-blue-500/30 p-8 rounded-[2.5rem] flex flex-col items-center justify-center text-center gap-4 group cursor-pointer hover:bg-blue-600/20 transition-all">
               <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center text-white">
                  <ChevronRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
               </div>
               <p className="text-white font-bold">View 200+ More Sales</p>
            </div>
          </div>
        </div>

        {/* CSS for custom scrollbar hide */}
        <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
          .mask-fade-edges {
            mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
          }
        `}</style>
      </section>

      {/* 5. Trust & Social Proof */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-end justify-between mb-16 gap-6">
            <div>
              <div className="flex items-center gap-3 text-blue-600 font-bold mb-4 uppercase tracking-widest text-sm">
                <Users className="w-5 h-5" />
                <span>Success Stories</span>
              </div>
              <h2 className="text-4xl font-bold">5000+ Happy Sellers</h2>
            </div>
            <div className="flex items-center gap-6 pb-2">
               <div className="text-center">
                 <p className="text-3xl font-bold">4.8/5</p>
                 <div className="flex text-yellow-400">
                   {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-current" />)}
                 </div>
                 <p className="text-xs text-slate-500 mt-1">Trustpilot Rating</p>
               </div>
               <div className="w-px h-12 bg-slate-200"></div>
               <div className="text-center">
                 <p className="text-3xl font-bold">99%</p>
                 <p className="text-xs text-slate-500 mt-3">Success Rate</p>
               </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, index) => (
              <motion.div 
                key={index} 
                whileHover={{ y: -10 }}
                className="bg-slate-50 rounded-3xl p-8 border border-slate-100 flex flex-col justify-between"
              >
                <div className="space-y-4">
                  <div className="flex text-yellow-400">
                    {[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= t.rating ? 'fill-current' : ''}`} />)}
                  </div>
                  <p className="text-slate-600 italic">"{t.text}"</p>
                </div>
                <div className="flex items-center gap-4 mt-8 pt-6 border-t border-slate-200">
                  <img src={t.avatar} className="w-12 h-12 rounded-full border border-slate-200" alt={t.name} />
                  <div>
                    <h4 className="font-bold text-slate-900">{t.name}</h4>
                    <p className="text-xs text-slate-500 font-medium">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Detailed Car Submission Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-white rounded-[2.5rem] shadow-2xl overflow-hidden">
            <div className="bg-blue-600 p-8 lg:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-2">Publish Your Listing</h2>
              <p className="opacity-80">Finalize your details and reach thousands of buyers instantly.</p>
            </div>
            <div className="p-8 lg:p-12 space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Owner Type</label>
                  <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none">
                    <option>1st Owner</option>
                    <option>2nd Owner</option>
                    <option>3rd Owner</option>
                    <option>4th+ Owner</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 flex justify-between">
                    Registration Number
                    {isRcVerified && <span className="text-green-600 text-[10px] font-black uppercase flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verified</span>}
                  </label>
                  <div className="relative">
                    <input 
                      type="text" 
                      placeholder="e.g. DL 01 AB 1234" 
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none" 
                    />
                    <button 
                      onClick={() => {
                        setIsVerifying(true);
                        setTimeout(() => {
                          setIsVerifying(false);
                          setIsRcVerified(true);
                          toast.success("Registration Number Verified!", { theme: "colored" });
                        }, 1500);
                      }}
                      className="absolute right-2 top-2 bottom-2 px-4 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-all"
                    >
                      {isVerifying ? "..." : "Verify"}
                    </button>
                  </div>
                </div>
                
                {/* 2.5 Ownership Document Upload */}
                <div className="md:col-span-2 p-6 bg-blue-50/50 border-2 border-dashed border-blue-100 rounded-[2rem] flex flex-col md:flex-row items-center gap-6">
                   <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 shrink-0">
                      <ShieldCheck className="w-10 h-10" />
                   </div>
                   <div className="flex-1 text-center md:text-left">
                      <h4 className="font-bold text-slate-900">Registration Certificate (RC)</h4>
                      <p className="text-xs text-slate-500">Upload your RC for instant trust badge and faster sale.</p>
                   </div>
                   <div className="shrink-0 w-full md:w-auto">
                      <input 
                        type="file" 
                        id="rc-upload" 
                        className="hidden" 
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if(file) {
                             setRcImage(URL.createObjectURL(file));
                             toast.info("RC Document Uploaded", { position: "bottom-right" });
                          }
                        }}
                      />
                      <button 
                        onClick={() => document.getElementById('rc-upload').click()}
                        className={`w-full md:w-auto px-8 py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${rcImage ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-white text-blue-600 border border-blue-200 shadow-sm'}`}
                      >
                        {rcImage ? <><CheckCircle2 className="w-4 h-4" /> RC Uploaded</> : <><Upload className="w-4 h-4" /> Upload RC</>}
                      </button>
                   </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Insurance Details</label>
                  <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none">
                    <option>Comprehensive</option>
                    <option>Third Party</option>
                    <option>Expired</option>
                    <option>Not Available</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">KM Driven</label>
                  <input type="number" placeholder="e.g. 15,000" className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none" />
                </div>
              </div>

              {/* 3. Condition Checklist - NEW SECTION */}
              <div className="space-y-6 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-lg">Detailed Condition Report</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Service History */}
                  <div className="space-y-3">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Service History</label>
                    <div className="flex flex-col gap-2">
                      {['Authorized', 'Local', 'No Records'].map(type => (
                        <button 
                          key={type}
                          onClick={() => setCondition({...condition, serviceHistory: type})}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-sm font-bold ${condition.serviceHistory === type ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'}`}
                        >
                          {type}
                          {condition.serviceHistory === type && <CheckCircle2 className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* AC Cooling */}
                  <div className="space-y-3">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">AC Cooling</label>
                    <div className="flex flex-col gap-2">
                      {['Excellent', 'Good', 'Needs Service'].map(type => (
                        <button 
                          key={type}
                          onClick={() => setCondition({...condition, acCooling: type})}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-sm font-bold ${condition.acCooling === type ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'}`}
                        >
                          {type}
                          {condition.acCooling === type && <CheckCircle2 className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tyre Condition */}
                  <div className="space-y-3">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest">Tyre Condition</label>
                    <div className="flex flex-col gap-2">
                      {['New', 'Good (50%+)', 'Budget/Worn'].map(type => (
                        <button 
                          key={type}
                          onClick={() => setCondition({...condition, tyreCondition: type})}
                          className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-sm font-bold ${condition.tyreCondition === type ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200'}`}
                        >
                          {type}
                          {condition.tyreCondition === type && <CheckCircle2 className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                   <div className="flex items-center justify-between p-5 bg-orange-50 rounded-2xl border border-orange-100">
                      <div>
                        <p className="font-bold text-slate-900">Any Major Accidents?</p>
                        <p className="text-xs text-slate-500">Full transparency helps sell faster</p>
                      </div>
                      <div className="flex bg-white/50 p-1 rounded-xl">
                        {['No', 'Yes'].map(opt => (
                           <button 
                            key={opt}
                            onClick={() => setCondition({...condition, accidents: opt})}
                            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${condition.accidents === opt ? 'bg-orange-600 text-white shadow-lg' : 'text-slate-500'}`}
                           >
                             {opt}
                           </button>
                        ))}
                      </div>
                   </div>

                   <div className="flex items-center justify-between p-5 bg-green-50 rounded-2xl border border-green-100">
                      <div>
                        <p className="font-bold text-slate-900">Active Bank Loan?</p>
                        <p className="text-xs text-slate-500">Required for NOC processing</p>
                      </div>
                      <div className="flex bg-white/50 p-1 rounded-xl">
                        {['No', 'Yes'].map(opt => (
                           <button 
                            key={opt}
                            onClick={() => setCondition({...condition, loan: opt})}
                            className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${condition.loan === opt ? 'bg-green-600 text-white shadow-lg' : 'text-slate-500'}`}
                           >
                             {opt}
                           </button>
                        ))}
                      </div>
                   </div>
                </div>

                {/* Key Features Checklist */}
                <div className="space-y-4 pt-6">
                  <label className="text-sm font-bold text-slate-700">Premium Features Installed</label>
                  <div className="flex flex-wrap gap-3">
                    {availableFeatures.map(f => (
                      <button
                        key={f}
                        onClick={() => handleFeatureToggle(f)}
                        className={`px-6 py-3 rounded-full border-2 font-bold text-xs transition-all ${condition.features.includes(f) ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-200 text-slate-500 hover:border-blue-200'}`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-slate-100">
                <label className="text-sm font-bold text-slate-700">Car Photos (Required)</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {['Front', 'Back', 'Interior'].map((side) => (
                    <div 
                      key={side}
                      onClick={() => document.getElementById(`upload-${side}`).click()}
                      className="border-2 border-dashed border-slate-200 rounded-3xl p-8 text-center group hover:border-blue-600 transition-all cursor-pointer bg-slate-50 relative overflow-hidden min-h-[200px] flex flex-col items-center justify-center"
                    >
                      <input 
                        type="file" 
                        id={`upload-${side}`} 
                        className="hidden" 
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setImages(prev => ({ ...prev, [side]: reader.result }));
                              toast.success(`${side} view photo selected successfully!`, {
                                position: "top-center",
                                autoClose: 2000,
                                theme: "colored",
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                      {images[side] ? (
                        <div className="absolute inset-0 w-full h-full">
                          <img src={images[side]} alt={side} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                            <p className="text-white font-bold text-sm">Change Photo</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3 group-hover:text-blue-600 group-hover:scale-110 transition-all" />
                          <p className="font-bold text-sm">{side} View</p>
                          <p className="text-[10px] text-slate-400 mt-1 italic">Click to upload</p>
                        </>
                      )}
                    </div>
                  ))}
                </div>

              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700">Description</label>
                <textarea rows="4" placeholder="Tell buyers more about your car's condition, features, or why you're selling..." className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none resize-none"></textarea>
              </div>

              <div className="flex items-center gap-3 bg-red-50 p-4 rounded-xl text-red-700 text-sm border border-red-100">
                 <Clock className="w-5 h-5 flex-shrink-0" />
                 <span>Cars with high-quality photos and detailed descriptions sell <span className="font-bold">3x faster</span> in your area.</span>
              </div>

              <div className="flex justify-center pt-4">
                <button className="w-full md:w-auto px-16 py-5 bg-red-600 hover:bg-red-700 text-white font-bold text-xl rounded-2xl shadow-xl shadow-red-200 transition-all transform hover:-translate-y-1">
                  Publish Listing Now
                </button>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 7. Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 px-4">
            <h2 className="text-4xl font-bold mb-4">Why Sellers Choose Us</h2>
            <p className="text-slate-500 lg:max-w-2xl mx-auto">We provide the most secure and transparent way to sell your used car at the best price.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => (
              <div key={i} className="p-8 rounded-[2rem] border border-slate-100 hover:shadow-2xl transition-all group">
                <div className={`w-14 h-14 ${f.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all`}>
                  {[<Zap />, <Users />, <Clock />, <ShieldCheck />][i]}
                </div>
                <h3 className="text-xl font-bold mb-3">{f.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Location Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="bg-slate-900 rounded-[3rem] p-12 lg:p-20 relative text-white flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2 space-y-6 relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm font-bold">
                <MapPin className="w-4 h-4" />
                <span>Available in Your City</span>
              </div>
              <h2 className="text-3xl lg:text-5xl font-bold">High Demand in <br /><span className="text-blue-400">Your Area Today</span></h2>
              <p className="text-slate-400 text-lg">We have over <span className="text-white font-bold">350+ buyers</span> actively searching for cars in your region right now. Prime time to list!</p>
              <div className="flex items-center gap-4 py-2">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => <div key={i} className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-700"></div>)}
                </div>
                <p className="text-sm text-slate-400 font-medium">Recently sold near you</p>
              </div>
            </div>
            <div className="w-full lg:w-1/2 relative bg-slate-800 rounded-3xl h-64 lg:h-80 overflow-hidden group">
               {/* Subtle Map Visualization */}
               <div className="absolute inset-0 opacity-20 bg-[url('https://www.google.com/maps/vt/pb=!1m4!1m3!1i10!2i512!3i512!2m3!1e0!2sm!3i420120488!3m8!2sen!3sin!5e1105!12m4!1e68!2m2!1sset!2sRoadmap!4e0!5m1!1f2!6m8!1e1!6m1!2i28!6m1!2i22!11m4!1e3!2m2!1sbi!2s1')] bg-cover"></div>
               <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <div className="w-12 h-12 bg-blue-600 rounded-full animate-ping opacity-20 absolute -inset-0"></div>
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center relative shadow-lg shadow-blue-500/50">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                  </div>
               </div>
               <div className="absolute bottom-6 left-6 right-6">
                 <div className="bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/10 flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-300 font-bold">TRENDING NOW</p>
                      <p className="text-sm font-bold underline">Check demand in my city</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-blue-400" />
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-4xl font-bold text-center mb-16">Got Questions?</h2>
          <div className="space-y-4">
            {[
              { q: "Is listing my car free?", a: "Absolutley! Listing your car on our platform is 100% free. We only earn a small success fee when you successfully close a deal with one of our verified buyers." },
              { q: "How do I get paid?", a: "Once you agree to a price with a buyer, we facilitate a secure instant payment directly into your bank account before you hand over the keys and documents." },
              { q: "How long does the process take?", a: "Most cars listed with high-quality photos get their first inspection request within 6 hours and are sold successfully within 24 to 48 hours." },
              { q: "Do you help with RC transfer?", a: "Yes! Our team assists with all the paperwork, including RC transfer and NOC, ensuring a complete and legal transfer of ownership." }
            ].map((item, i) => (
              <div key={i} className="border border-slate-100 rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex items-center justify-between px-8 py-6 text-left hover:bg-slate-50 transition-all"
                >
                  <span className="font-bold text-lg">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} />
                </button>
                {faqOpen === i && (
                  <div className="px-8 pb-6 text-slate-500 leading-relaxed animate-fadeIn">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. Contact / Support Section */}
      <section className="py-24 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-[3rem] p-8 lg:p-16 shadow-xl border border-slate-100 flex flex-col lg:flex-row items-center gap-16">
            <div className="w-full lg:w-1/2 space-y-8">
              <h2 className="text-4xl font-bold">We're Here <br />To Help You</h2>
              <p className="text-slate-500 text-lg">Our dedicated car selling experts are available 24/7 to guide you through the process and ensure you get the best deal.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">Chat with us</p>
                    <p className="text-sm text-slate-500">Live Support 24/7</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center text-red-600">
                    <PhoneCall className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">Call 1800-SPARE-PE</p>
                    <p className="text-sm text-slate-500">Toll Free Support</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="bg-slate-50 p-8 rounded-3xl border border-slate-200">
                <form className="space-y-4">
                  <input type="text" placeholder="Your Name" className="w-full px-5 py-4 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
                  <input type="email" placeholder="Your Email" className="w-full px-5 py-4 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" />
                  <textarea rows="3" placeholder="How can we help?" className="w-full px-5 py-4 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"></textarea>
                  <button className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-200">
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. Final CTA Section - Redesigned */}
      <section className="py-24 relative overflow-hidden bg-slate-900 mx-4 lg:mx-8 mb-12 rounded-[3.5rem]">
        {/* Background Gradients */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600 rounded-full blur-[120px] opacity-20"></div>
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-red-600 rounded-full blur-[100px] opacity-10"></div>
        </div>

        <div className="container mx-auto px-4 text-center text-white relative z-10 py-8 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-blue-400 text-sm font-bold mb-8 backdrop-blur-sm">
              <Zap className="w-4 h-4 fill-current" />
              <span>Free Listing • Verified Buyers • Instant Payment</span>
            </div>
            
            <h2 className="text-4xl lg:text-6xl font-black mb-8 tracking-tight leading-[1.2] text-white">
              Ready to sell your car <br/> 
              <span className="text-blue-400">for the best price?</span>
            </h2>

            
            <p className="text-lg lg:text-xl text-slate-400 mb-12 max-w-2xl mx-auto font-medium leading-relaxed">
              Join 50,000+ satisfied sellers across India. Start your listing today and get connected with 5000+ verified buyers instantly.
            </p>

            <div className="flex flex-col items-center justify-center gap-8">
              <motion.button 
                whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(37, 99, 235, 0.4)" }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-12 py-6 bg-blue-600 text-white font-bold text-xl rounded-2xl transition-all overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <span className="relative z-10 flex items-center gap-3">
                  Start Selling Now
                  <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
              
              <div className="flex items-center gap-8 pt-4 border-t border-white/5 w-full max-w-md justify-center">
                 <div className="text-center">
                    <p className="text-2xl font-bold">4.8/5</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">User Rating</p>
                 </div>
                 <div className="w-px h-8 bg-white/10"></div>
                 <div className="text-center">
                    <p className="text-2xl font-bold">50K+</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Cars Sold</p>
                 </div>
                 <div className="w-px h-8 bg-white/10"></div>
                 <div className="text-center">
                    <p className="text-2xl font-bold">60s</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">To List</p>
                 </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Footer Branding (Simplified) */}
      <footer className="py-12 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-6">
           <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                 <Car className="text-white w-6 h-6" />
              </div>
              <span className="text-2xl font-black tracking-tighter italic">Spare<span className="text-red-600">Pe</span></span>
           </div>
           <p className="text-slate-400 text-sm">© 2026 SparePe Automotive Service Platform. All rights reserved.</p>
           <div className="flex gap-8 text-slate-500 font-bold text-sm">
             <Link to="/privacy-policy" className="hover:text-blue-600">Privacy</Link>
             <Link to="/terms-of-use" className="hover:text-blue-600">Terms</Link>
             <Link to="/contact" className="hover:text-blue-600">Contact</Link>
           </div>
        </div>
      </footer>

      {/* Style for Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .premium-calendar, .full-modern-calendar {
          border: none !important;
          background: white !important;
          border-radius: 24px !important;
          font-family: inherit !important;
          padding: 0 !important;
          width: 100% !important;
          display: block !important;
        }
        .react-datepicker {
          border: none !important;
          width: 100% !important;
          display: flex !important;
          flex-direction: column !important;
        }
        .react-datepicker__month-container {
          width: 100% !important;
          float: none !important;
        }
        .react-datepicker__header {
          background: white !important;
          border-bottom: 1px solid #f1f5f9 !important;
          padding-top: 0 !important;
          border-top-left-radius: 24px !important;
          border-top-right-radius: 24px !important;
        }
        .react-datepicker__day-names {
          display: flex;
          justify-content: space-around;
          padding: 10px 0;
          margin-bottom: 0 !important;
        }
        .react-datepicker__day-name {
          font-weight: 700 !important;
          color: #94a3b8 !important;
          text-transform: uppercase !important;
          font-size: 0.75rem !important;
          letter-spacing: 0.05em !important;
          width: 3rem !important;
        }
        .react-datepicker__month {
          margin: 1.5rem 0.5rem !important;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .react-datepicker__week {
          display: flex;
          justify-content: space-around;
        }
        .react-datepicker__day {
          width: 3rem !important;
          height: 3rem !important;
          line-height: 3rem !important;
          margin: 0 !important;
          font-weight: 600 !important;
          font-size: 0.95rem !important;
          color: #1e293b !important;
          border-radius: 12px !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        .react-datepicker__day:hover {
          background-color: #f1f5f9 !important;
          color: #2563eb !important;
          transform: scale(1.1);
        }
        .react-datepicker__day--selected {
          background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%) !important;
          color: white !important;
          box-shadow: 0 10px 15px -3px rgba(37, 99, 235, 0.3) !important;
          transform: scale(1.1) translateY(-2px);
        }
        .react-datepicker__day--outside-month {
          color: #cbd5e1 !important;
          opacity: 0.4;
        }
        .react-datepicker__day--disabled {
          color: #e2e8f0 !important;
          cursor: not-allowed !important;
        }
        .react-datepicker__day--disabled:hover {
          background-color: transparent !important;
          transform: none !important;
        }
        .react-datepicker__day--today {
          color: #2563eb !important;
          background-color: #eff6ff !important;
        }

      `}</style>

    </div>
  );
};

export default SellYourCar;
